import { CSSObject } from '@mui/material';

/**
 * Style for interactive elements like list and buttons. Usage in theme as:
 *  ...getInteractiveStyle(
 *   { backgroundColor: 'transparent' },
 *   { backgroundColor: theme.palette.surface.primary },
 *   { backgroundColor: theme.palette.surface.secondary },
 *  ),
 *
 * @param defaultStyle - default applied style
 * @param hoverStyle - style applied on hover, focus, and selected (desktop only)
 * @param clickedStyle - (optional) style applied with click, defaults to defaultStyle
 * @param mobileStyle - (optional) extra style applied on mobile
 * @param focusSelector - focus visible selector
 */
export const getInteractiveStyle = (
  defaultStyle: CSSObject,
  hoverStyle: CSSObject,
  clickedStyle?: CSSObject,
  mobileStyle?: CSSObject,
  focusSelector = '.Mui-focusVisible',
): CSSObject => ({
  ...defaultStyle,
  // hover, focus, selected
  [`&:hover, &${focusSelector}, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected${focusSelector}, &[aria-selected="true"], &[aria-selected="true"]:hover, &[aria-selected="true"]${focusSelector}`]:
    {
      ...hoverStyle,
    },
  // click effect
  '&:active, &.Mui-selected:active, &[aria-selected="true"]:active': {
    ...(clickedStyle || defaultStyle),
  },
  // mobile behavior (detected by pointer to include most of the devices)
  '@media (pointer: coarse)': {
    // remove hover state
    [`&:hover:not(:active):not(${focusSelector}):not(.Mui-selected):not([aria-selected="true"])`]: {
      ...defaultStyle,
    },
    // in case of no clickedStyle, use hover style
    ...(!clickedStyle && {
      '&:active, &.Mui-selected:active, &[aria-selected="true"]:active': {
        ...hoverStyle,
      },
    }),
    // optional extra styling
    ...mobileStyle,
  },
});
